/**
 * Pure adoption entry point (batch-003 `castAdapter.ts`). It does not import the cast registry, so
 * `hushNookCast.ts` can call it without a cycle.
 */
import { validateTokens } from '../../../shared/assets/hushStatic';
import { one, record } from '../../../shared/assets/hushStatic/validation';
import { resolvePlayerEquipmentVisuals } from '../../../shared/equipmentVisuals';
import { AppearanceHash } from '../../../shared/utils/AppearanceHash';
import type { HushAppearance, HumanoidAppearance, Tokens, WardrobeResources } from './types';
import { REVISION } from './types';
import { validateAppearance, validateWardrobeRecipe } from './validate';
import { mallowSeaGlass } from './geometry';

export function adaptWardrobeAppearance(
  base: HumanoidAppearance, raw: unknown, tokens: Tokens, resources: WardrobeResources, overrides: unknown = {},
): HushAppearance {
  const recipe = validateWardrobeRecipe(raw);
  validateTokens(tokens);
  const o = record(overrides, 'overrides');
  for (const key of Object.keys(o)) if (!['shape', 'bodySize'].includes(key)) throw new Error(`unknown appearance override ${key}`);
  if (o.shape !== undefined) one(o.shape, ['classic', 'slim', 'round', 'wide'], 'body shape');
  if (o.bodySize !== undefined) one(o.bodySize, ['small', 'normal', 'large'], 'body size');
  if (!base.humanoid?.garment) throw new Error('wardrobe base requires an existing garment');
  const p = tokens.palettes[recipe.paletteId];
  const humanoid: HushAppearance['humanoid'] = {
    ...base.humanoid, props: [], fit: true,
    hushWardrobe: { schema: 'hush.rig-accessory.v1', revision: REVISION, recipe, tokens, resources },
  };
  // These fields are the cast's owned wardrobe changes; body, face and rig stay current.
  if (recipe.params.look === 'pip') {
    humanoid.hair = undefined;
    humanoid.garment = { ...base.humanoid.garment, color: p.woodBase, trimColor: p.paperShadow };
  } else {
    humanoid.cloak = { color: mallowSeaGlass(p), claspColor: p.paperShadow };
    humanoid.garment = { ...base.humanoid.garment, trimColor: p.stoneBase };
  }
  // The explicit empty equipment state survives JSON (a `weapon: undefined` would drop and let DEFAULT_GHOST's sword back in).
  const appearance = {
    ...base, ...o, equipmentVisuals: resolvePlayerEquipmentVisuals({}), eyes: 'round' as const,
    mouth: recipe.params.pose === 'talk' ? 'o' as const : 'smile' as const, humanoid,
  } as HushAppearance;
  validateAppearance(appearance);
  return appearance;
}

/** The existing recursive appearance hash already includes the complete added config. */
export const appearanceCacheIdentity = (a: HushAppearance): string => AppearanceHash.generate(a);
